import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';

/**
 * Zugriff auf AppSync.
 *
 * Der Client wird erst nach `Amplify.configure()` erzeugt — deshalb verzögert
 * und nicht als Modulkonstante.
 */

let client: ReturnType<typeof generateClient<Schema>> | null = null;

export const api = (): ReturnType<typeof generateClient<Schema>> => {
  client ??= generateClient<Schema>({ authMode: 'userPool' });
  return client;
};

export const apiZuruecksetzen = (): void => {
  client = null;
};

export type ApiSchema = Schema;

/** Fehler, bei denen ein erneuter Versuch nichts bringt. */
const DAUERHAFTE_MUSTER = [
  'Unauthorized',
  'not authorized',
  'ValidationException',
  'ConditionalCheckFailedException',
  // Wortlaut, mit dem AppSync die Bedingungsprüfung von DynamoDB weitergibt.
  // Der Name der Exception steht darin nicht — das Muster darüber greift nicht.
  // Die Outbox versucht bei diesem Fehler zuerst den Gegenweg (anlegen ⇄
  // ändern); scheitert auch der, ist Wiederholen sinnlos.
  'The conditional request failed',
  'The variables input contains a field',
  'Validation error',
];

export const istDauerhafterFehler = (fehler: unknown): boolean => {
  const text = fehlerText(fehler);
  return DAUERHAFTE_MUSTER.some((muster) => text.toLowerCase().includes(muster.toLowerCase()));
};

export const istNetzfehler = (fehler: unknown): boolean => {
  const text = fehlerText(fehler).toLowerCase();
  return (
    text.includes('network') ||
    text.includes('failed to fetch') ||
    text.includes('load failed') ||
    text.includes('networkerror') ||
    text.includes('timeout')
  );
};

export const fehlerText = (fehler: unknown): string => {
  if (!fehler) return 'Unbekannter Fehler';
  if (typeof fehler === 'string') return fehler;
  if (fehler instanceof Error) return fehler.message;
  const kandidat = fehler as { errors?: Array<{ message?: string }>; message?: string };
  if (Array.isArray(kandidat.errors) && kandidat.errors.length > 0) {
    return kandidat.errors.map((eintrag) => eintrag.message ?? '').join('; ');
  }
  return kandidat.message ?? JSON.stringify(fehler).slice(0, 300);
};

/** Wirft, wenn eine Amplify-Antwort GraphQL-Fehler enthält. */
export const pruefeAntwort = <T>(antwort: { data?: T | null; errors?: unknown }): T => {
  const fehler = (antwort as { errors?: Array<{ message?: string }> }).errors;
  if (fehler && fehler.length > 0) {
    throw new Error(fehler.map((eintrag) => eintrag.message ?? 'Fehler').join('; '));
  }
  if (antwort.data === null || antwort.data === undefined) {
    throw new Error('Der Server hat keine Daten zurückgeliefert.');
  }
  return antwort.data;
};

/** Läuft eine paginierte Liste vollständig durch. */
export const alleSeiten = async <T>(
  abfragen: (
    token: string | null,
  ) => Promise<{ data: T[]; nextToken?: string | null; errors?: unknown }>,
  maxSeiten = 50,
): Promise<T[]> => {
  const gesammelt: T[] = [];
  let token: string | null = null;
  let seite = 0;

  do {
    const antwort = await abfragen(token);
    const fehler = (antwort as { errors?: Array<{ message?: string }> }).errors;
    if (fehler && fehler.length > 0) {
      throw new Error(fehler.map((eintrag) => eintrag.message ?? 'Fehler').join('; '));
    }
    gesammelt.push(...(antwort.data ?? []));
    token = antwort.nextToken ?? null;
    seite += 1;
  } while (token && seite < maxSeiten);

  return gesammelt;
};
